import { useCallback, useEffect, useState } from "react";
import { getTransactionSummaryAction } from "../../Actions/TransactionActions";
import { MyWifiIcon, MyWifiOffIcon, MyRefreshIcon, MyReceiptLongIcon, MyTaskAltIcon, MyHourglassEmptyIcon, MyDoNotDisturbOnIcon } from "../../Assets/svg/MyIcons";

const StatCard = ({ title, count, amount, icon: Icon, color, loading, colClass }) => {
  return (
    <div className={colClass}>
      <div className="card h-100 shadow-sm rounded-3">
        {loading ? (
          <div className="d-flex align-items-center justify-content-center p-10">
            <div
              className="spinner-border text-primary"
              role="status"
              style={{ width: 32, height: 32 }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="card-body d-flex flex-column justify-content-between p-5">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-5">
              <div
                className="symbol w-55px h-55px d-flex justify-content-center align-items-center rounded-circle border"
                style={{
                  backgroundColor: `${color}20`,
                  borderColor: `${color}40`,
                  color,
                }}
              >
                <Icon size={28} />
              </div>
              <div className="fs-2x fw-bold text-dark">{count?.toLocaleString()}</div>
            </div>

            {/* Footer */}
            <div className="d-flex justify-content-between align-items-center">
              <div className="fw-semibold text-gray-600 fs-5">{title}</div>
              {amount !== undefined && <div className="fs-6 fw-bold text-dark">CI$ {amount}</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const TransactionCards = ({
  handleSnackBar = () => {},
  startDate,
  endDate,
  statusFilter,
  tenantFilter,
  searchValue,
  amountFilter,
  shouldRefresh = 0,
}) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: { count: 0, amount: 0 },
    success: { count: 0, amount: 0 },
    pending: { count: 0, amount: 0 },
    refund: { count: 0, amount: 0 },
    failed: { count: 0, amount: 0 },
    online: { count: 0, amount: 0 },
    offline: { count: 0, amount: 0 },
  });

  const safe = (val, fallback = 0) => val ?? fallback;

  const mapData = useCallback(
    (data) => ({
      total: {
        count: safe(data?.totalTransactionCount),
        amount: safe(data?.totalTransactionAmount),
      },
      success: {
        count: safe(data?.totalSuccessTransactionCount),
        amount: safe(data?.totalSuccessTransactionAmount),
      },
      pending: {
        count: safe(data?.totalPendingTransactionCount),
        amount: safe(data?.totalPendingTransactionAmount),
      },
      refund: {
        count: safe(data?.totalRefundTransactionCount),
        amount: safe(data?.totalRefundTransactionAmount),
      },
      failed: {
        count: safe(data?.totalFailedTransactionCount),
        amount: safe(data?.totalFailedTransactionAmount),
      },
      online: {
        count: safe(data?.totalOnlinePaymentsCount),
        amount: safe(data?.totalOnlinePaymentsAmount),
      },
      offline: {
        count: safe(data?.totalOfflinePaymentsCount),
        amount: safe(data?.totalOfflinePaymentsAmount),
      },
    }), []);

  const fetchSummary = useCallback(async (filters) => {
      setLoading(true);
      try {
        const res = await getTransactionSummaryAction(filters);
        if (res.status === 200) {
          setStats(mapData(res.data));
        }
      } catch (error) {
        console.error("Transaction summary error:", error);
        handleSnackBar("error", error?.response?.data?.message || "Failed to load transactions.");
      } finally {
        setLoading(false);
      }
    },
    [mapData, handleSnackBar]
  );

  useEffect(() => {
    if (shouldRefresh > 0) {
      const filters = {
        startDate,
        endDate,
        status: statusFilter,
        tenantFilter,
        searchValue,
        amount: amountFilter,
      };
      fetchSummary(filters);
    }
  }, [shouldRefresh]);

  useEffect(() => {
    const filters = {
      startDate,
      endDate,
      status: statusFilter,
      tenantFilter,
      searchValue,
      amount: amountFilter,
    };
    fetchSummary(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cards = [
    {
      title: "Total Transactions",
      count: stats.total.count,
      amount: stats.total.amount,
      icon: MyReceiptLongIcon,
      color: "#6366f1",
      colClass: "col-12 col-sm-6 col-lg-4 mb-4",
    },
    {
      title: "Online Payments",
      count: stats.online.count,
      amount: stats.online.amount,
      icon: MyWifiIcon,
      color: "#8b5cf6",
      colClass: "col-12 col-sm-6 col-lg-4 mb-4",
    },
    {
      title: "Offline Payments",
      count: stats.offline.count,
      amount: stats.offline.amount,
      icon: MyWifiOffIcon,
      color: "#6b7280",
      colClass: "col-12 col-sm-6 col-lg-4 mb-4",
    },
    {
      title: "Successful",
      count: stats.success.count,
      amount: stats.success.amount,
      icon: MyTaskAltIcon,
      color: "#10b981",
      colClass: "col-12 col-sm-6 col-lg-3 mb-4",
    },
    {
      title: "Pending",
      count: stats.pending.count,
      amount: stats.pending.amount,
      icon: MyHourglassEmptyIcon,
      color: "#f59e0b",
      colClass: "col-12 col-sm-6 col-lg-3 mb-4",
    },
    {
      title: "Failed",
      count: stats.failed.count,
      amount: stats.failed.amount,
      icon: MyDoNotDisturbOnIcon,
      color: "#ef4444",
      colClass: "col-12 col-sm-6 col-lg-3 mb-4",
    },
    {
      title: "Refund",
      count: stats.refund.count,
      amount: stats.refund.amount,
      icon: MyRefreshIcon,
      color: "#3b82f6",
      colClass: "col-12 col-sm-6 col-lg-3 mb-4",
    },
  ];

  return (
    <div className="row g-4 mb-5">
      {cards.map((c, idx) => (
        <StatCard key={idx} {...c} loading={loading} />
      ))}
    </div>
  );
};

export default TransactionCards;